import { Component, OnInit, Input, OnChanges, ViewChild, OnDestroy } from '@angular/core';
import { Group, Course, Profile } from 'core/models';
import {
  ModalComponent,
  ChangeGroupModalComponent,
  StoreService,
  LicensesService,
  PopUpService,
  GroupsService,
  PersonalizedEmailComponent,
  JobsService,
  ProfileService,
  JournalService,
  CoursesService
} from '../../../core';
import { TranslateService } from '@ngx-translate/core';
import { BUTTON_ACTIONS_GROUP_LICENSES } from '../../assets/constants';
import { Subscription } from 'rxjs';
import { LicensesListComponent } from 'app/licenses/licenses-list/licenses-list.component';
import { LicensesListService } from 'app/licenses/licenses-list/licenses-list.service';

@Component({
  selector: 'lms-group-licenses',
  templateUrl: './group-licenses.component.html',
  styleUrls: ['./group-licenses.component.scss'],
  providers: [LicensesListService]
})
export class GroupLicensesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() group: Group;
  @Input() course: Course;

  @ViewChild('groupLicensesList', { static: false }) public groupLicensesList: LicensesListComponent;
  @ViewChild('activateModal', { static: false }) public activateModal: ModalComponent;
  @ViewChild('changeGroupModal', { static: false }) public changeGroupModal: ChangeGroupModalComponent;
  @ViewChild('personalizedEmail', { static: false }) public personalizedEmail: PersonalizedEmailComponent;

  public groupLicenseslistId = 'GROUP_LICENSES_LIST';
  public filters: any;
  public licensesExtensionOptions: any;
  public jobs: any;
  public dataSource: any;

  public action: string = '';
  public bulkActions: any[];
  public user: any;
  public canLicensesBeExtended: boolean = true;
  public listSubscription: Subscription;
  public selection: any;
  private loggedUserSubscription: Subscription = new Subscription();

  constructor(
    public licensesService: LicensesService,
    public licensesListService: LicensesListService,
    public journalService: JournalService,
    public translate: TranslateService,
    public jobsService: JobsService,
    public popupService: PopUpService,
    public coursesService: CoursesService,
    public groupsService: GroupsService,
    public profileService: ProfileService
  ) {
    this.jobs = this.jobsService.jobTypes;
  }

  public ngOnInit(): void {
    this.bulkActions = [...BUTTON_ACTIONS_GROUP_LICENSES];
    this.checkGroupStatements();

    this.canLicensesBeExtended = !this.course.archived;
    this.loggedUserSubscription = this.profileService.getLoggedUser$().subscribe((loggedUser: Profile) => {
      this.user = loggedUser;
    });
    this.listSubscription = StoreService.get(this.groupLicenseslistId).subscribe((dataSource) => {
      this.dataSource = dataSource;
    });
  }

  public ngOnChanges(): void {
    this.filters = { groupId: this.group.id, kind: 'normal' };
  }

  public ngOnDestroy(): void {
    this.loggedUserSubscription.unsubscribe();
    this.listSubscription.unsubscribe();
  }

  onSelectedLicenses(selection) {
    this.selection = selection;
    this.setLicenseExtensionOptions();
  }

  setLicenseExtensionOptions() {
    const validLicenses = this.selection.selected
      ? this.selection.selected.filter(
          (license) => license.kind === 'normal' && ['beforeDemo', 'afterDemo', 'created'].includes(license.status)
        )
      : [];

    const maxLicenseEndDate = validLicenses.reduce((currentMaxEndDate, license) => {
      const licenseEndDate = new Date(license.endDate);
      return licenseEndDate.getTime() > currentMaxEndDate.getTime() ? licenseEndDate : currentMaxEndDate;
    }, new Date());

    this.licensesExtensionOptions = {
      action: 'licenses.extend',
      clientId: this.group.clientId,
      extendableLicenses: validLicenses,
      currentEndDate: new Date(maxLicenseEndDate),
      groupEndDate: new Date(this.group.endDate),
      hasBonus: this.group.hasBonus,
      groupOriginalEndDate: new Date(this.group.originalEndDate),
      timezone: this.group.timezone
    };
  }

  selectAction(key) {
    switch (key) {
      case '1':
        this.openModal(this.jobs.SEND_START_CLASS_LICENSES_BULK);
        break;
      case '2':
        this.action = this.jobs.DOWNLOAD_CLASS_DIPLOMAS_BULK;
        this.confirmAction();
        break;
      case '3':
        this.action = this.jobs.DOWNLOAD_LICENSES_REPORT;
        this.confirmAction({ lang: this.user.lang });
        break;
      case '4':
        this.changeGroupModal.openModal();
        break;
      case '5':
        this.personalizedEmail.openModal();
        break;
    }
  }

  async extendSelectedLicenses(endDate: any) {
    const groupId = this.group.id;
    const groupEndDate = this.group.endDate;

    if (groupEndDate && new Date(endDate) > new Date(groupEndDate)) {
      // Extend group end date as it is before new license end date
      await this.groupsService
        .extend(groupId, endDate)
        .then()
        .catch((err) => {
          this.popupService.showError({
            text: err.serverMessage
          });
        });
    }

    const data = {
      licenses: this.getSelectedIds(),
      endDate,
      jobType: this.jobs.EXTEND_LICENSES_BULK
    };
    this.updateLicensesBulk(data);
  }

  changeGroupLicenses(newGroupId) {
    const data = {
      licenses: this.getSelectedIds(),
      jobType: this.jobs.CHANGE_CLASS_LICENSES_BULK,
      newGroupId
    };
    this.updateLicensesBulk(data);
  }

  openModal(action: string) {
    this.action = action;
    this.activateModal.open();
  }

  confirmModal() {
    this.confirmAction();
    this.activateModal.close();
  }

  confirmAction(options: any = {}) {
    const data = {
      licenses: this.getSelectedIds(),
      jobType: this.action,
      ...options
    };
    this.updateLicensesBulk(data);
  }

  sendEmail(email) {
    const data: any = {
      body: email.body,
      title: email.title,
      licenses: this.getSelectedIds(),
      fromName: email.fromName,
      jobType: this.jobs.SEND_PERSONALIZED_EMAIL_BULK
    };
    if (email.autosend) {
      // A copy of the personalized email will be sent to the admin
      data.userData = {
        id: this.user.id,
        email: this.user.email
      };
    }
    this.updateLicensesBulk(data);
  }

  updateLicensesBulk(data: any) {
    const bulkData = {
      query: {
        groupId: this.selection.selectAll ? this.group.id : null,
        search: this.dataSource.search,
        ...this.licensesListService.getDatasourceFilters(this.dataSource)
      },
      ...data
    };
    if (this.selection.selectAll) {
      delete bulkData.licenses;
      if (this.selection.excluded.size) {
        bulkData.query.excludedLicenses = this.getExcludedIds();
      }
    } else {
      delete bulkData.query;
    }
    this.deleteSelection();
    this.licensesService.updateLicensesBulk(bulkData).then(async (res: any) => {
      const jobSubscription = this.jobsService.manageJob(res.jobId, bulkData.jobType).subscribe((job) => {
        if (job.status === 'finished' || job.status === 'fatal-error') {
          jobSubscription.unsubscribe();
          this.dataSource.refreshData();
        }
      });
    });
  }

  checkGroupStatements() {
    this.journalService.checkGroupStatements(this.group.id).then((res) => {
      if (this.group.hasBonus) {
        this.bulkActions.splice(3, 1);
      }
      if ((this.group.type === 'regular' && !res.result) || this.coursesService.checkOldGames(this.group)) {
        this.bulkActions = this.bulkActions.filter((item) => item.key !== '3');
      }
    });
  }

  deleteSelection() {
    this.groupLicensesList.setSelectAll(false);
  }

  private getSelectedIds(): Array<any> {
    return this.selection.selected.map((license) => ({ _id: license.id }));
  }

  private getExcludedIds(): Array<any> {
    return [...this.selection.excluded.keys()].map((_id) => ({ _id }));
  }
}
